'use client'

import { memo } from 'react'
import { type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Preset clip positions: each tile gets a different peek angle */
export type NinxlCapabilityAssetPlacement =
  | 'right-mid-cw'
  | 'right-high-ccw'
  | 'right-low-cw'
  | 'right-center-ccw'
  | 'corner-bottom-cw'
  | 'corner-top-ccw'
  | 'left-mid-ccw'
  | 'right-deep-cw'
  | 'right-upper-cw'

const PLACEMENT_STYLES: Record<NinxlCapabilityAssetPlacement, string> = {
  'right-mid-cw':
    'absolute -right-4 top-[52%] w-[4.75rem] -translate-y-1/2 rotate-[10deg] sm:-right-3 sm:w-[5.25rem]',
  'right-high-ccw':
    'absolute -right-5 top-[18%] w-[4.5rem] rotate-[-14deg] sm:-right-3 sm:w-[5rem]',
  'right-low-cw':
    'absolute -right-3 bottom-[8%] w-[5rem] rotate-[16deg] sm:-right-2 sm:w-[5.5rem]',
  'right-center-ccw':
    'absolute -right-6 top-[42%] w-[4.25rem] -translate-y-1/2 rotate-[-8deg] sm:-right-4 sm:w-[4.75rem]',
  'corner-bottom-cw':
    'absolute -right-2 -bottom-3 w-[5.25rem] rotate-[22deg] sm:w-[5.75rem]',
  'corner-top-ccw':
    'absolute -right-1 top-[-0.35rem] w-[4.5rem] rotate-[-18deg] sm:-right-2 sm:w-[5rem]',
  'left-mid-ccw':
    'absolute -left-5 top-[58%] w-[4.75rem] -translate-y-1/2 rotate-[-12deg] sm:-left-3 sm:w-[5.25rem]',
  'right-deep-cw':
    'absolute -right-8 top-[62%] w-[5.5rem] -translate-y-1/2 rotate-[6deg] sm:-right-5 sm:w-[6rem]',
  'right-upper-cw':
    'absolute -right-4 top-[32%] w-[4rem] -translate-y-1/2 rotate-[14deg] sm:w-[4.5rem]',
}

const HOVER_NUDGE: Record<'left' | 'right', string> = {
  right: 'group-hover:translate-x-[-2px]',
  left: 'group-hover:translate-x-[2px]',
}

function placementSide(placement: NinxlCapabilityAssetPlacement): 'left' | 'right' {
  return placement === 'left-mid-ccw' ? 'left' : 'right'
}

/** Half-visible icon asset: same clip/hover feel as /nin product device mocks */
export const NinxlCapabilityAssetMock = memo(function NinxlCapabilityAssetMock({
  icon: Icon,
  placement = 'right-mid-cw',
  className,
}: {
  icon: LucideIcon
  placement?: NinxlCapabilityAssetPlacement
  className?: string
}) {
  const side = placementSide(placement)

  return (
    <div
      className={cn('ninxl-capability-asset-mock pointer-events-none absolute inset-0 overflow-hidden', className)}
      aria-hidden
      data-placement={placement}
    >
      <div
        className={cn(
          PLACEMENT_STYLES[placement],
          'opacity-45 transition duration-300',
          HOVER_NUDGE[side],
          'group-hover:opacity-60 md:opacity-50 md:group-hover:opacity-65',
        )}
      >
        <div className="ninxl-pixel-border relative flex aspect-square items-center justify-center bg-[color:var(--ninxl-theme-bg-frame)] p-2.5 shadow-[3px_3px_0_0_var(--ninxl-8bit-dim)]">
          <Icon className="h-full w-full text-[color:var(--ninxl-brand-teal)]" strokeWidth={1.35} />
        </div>
      </div>
    </div>
  )
})
